/* ---------------------------------------------------------------------------
   Bangladesh’s national outline, bundled.

   The register’s map draws OpenStreetMap tiles, which need a network. This is
   what it draws when they do not arrive — see src/data/pakistan.ts, which
   carries the whole argument for bundling one, and src/map/nepalMap.ts for how
   the two layers relate.

   PROVENANCE. Natural Earth 1:10m Admin 0 Countries, **Bangladesh point-of-view
   edition** (`ne_10m_admin_0_countries_bdg`), public domain —
   https://www.naturalearthdata.com/about/terms-of-use/. The largest ring of
   Bangladesh’s polygon, simplified by Douglas–Peucker at the same 0.0126°
   tolerance as the other outlines (about 1.4 km), from 1833 points to the 494
   below, and rounded to four decimal places (about 11 m).

     npm run gen:outline -- --country BGD --pov bdg

   prints exactly the array below. Natural Earth spells the edition `bdg`, not
   the ISO `bgd`; the ISO spelling is a 404.

   THE POINT-OF-VIEW EDITION, by the rule src/data/pakistan.ts sets out: a
   tenant’s map draws its own government’s point of view where Natural Earth
   publishes one. It adjudicates nothing, it is not a survey boundary, and
   nothing in this application reads it for any purpose but drawing a backdrop
   when no tile arrives.

   ── WHAT IS DROPPED ──

   The largest ring only, as for every other tenant: `TenantGeography.outline`
   is one closed ring. The generator reported twenty-one others, every one an
   island of the delta or the Bay of Bengal — the largest is Bhola (62 points,
   90.55–90.86°E, 21.99–22.79°N), then Maheshkhali, Manpura, Hatiya and the
   chars around them. A bridge on one of them still plots; it plots over water
   on the offline backdrop, which is the backdrop’s limitation and not the
   register’s. Drawing them wants an outline type with more than one ring.
   --------------------------------------------------------------------------- */

import type { LngLat } from './nepal.ts';
import type { GeoExtent } from '../tenant/types.ts';

/**
 * The outline, as a closed ring — the last point repeats the first, so a
 * consumer can draw it as a polyline without joining the ends itself.
 */
export const BANGLADESH_OUTLINE: readonly LngLat[] = [
  [92.5759, 21.9776],
  [92.593, 21.8869],
  [92.5756, 21.734],
  [92.5902, 21.4955],
  [92.6392, 21.3187],
  [92.6338, 21.2822],
  [92.5706, 21.2473],
  [92.5245, 21.3611],
  [92.4003, 21.371],
  [92.3547, 21.4518],
  [92.3227, 21.4619],
  [92.2845, 21.4191],
  [92.2363, 21.4171],
  [92.1783, 21.2997],
  [92.175, 21.1756],
  [92.2651, 21.0611],
  [92.2615, 20.9403],
  [92.3333, 20.7677],
  [92.3255, 20.7387],
  [92.2097, 20.9308],
  [92.1863, 21.0058],
  [92.0525, 21.1548],
  [92.0453, 21.2995],
  [91.953, 21.4418],
  [91.9829, 21.4888],
  [92.0179, 21.4931],
  [91.99, 21.562],
  [92.0358, 21.6404],
  [92.0293, 21.7049],
  [92.0007, 21.6498],
  [91.9839, 21.6501],
  [91.9839, 21.7054],
  [91.9627, 21.6895],
  [91.9497, 21.716],
  [91.953, 21.7383],
  [91.99, 21.76],
  [91.9603, 21.7638],
  [91.99, 21.7948],
  [91.8993, 21.7895],
  [91.9497, 21.8842],
  [91.9157, 21.8625],
  [91.8552, 22.1092],
  [91.8535, 22.1436],
  [91.8672, 22.1374],
  [91.8533, 22.1487],
  [91.8399, 22.1169],
  [91.813, 22.1803],
  [91.8399, 22.2466],
  [91.8088, 22.3063],
  [91.874, 22.336],
  [91.9081, 22.4247],
  [91.8877, 22.4247],
  [91.8775, 22.3633],
  [91.7914, 22.3019],
  [91.8259, 22.2485],
  [91.7871, 22.2379],
  [91.7437, 22.4111],
  [91.676, 22.5346],
  [91.5481, 22.7003],
  [91.4624, 22.7619],
  [91.4905, 22.9045],
  [91.4624, 22.8334],
  [91.4168, 22.8],
  [91.4147, 22.7537],
  [91.3464, 22.7748],
  [91.3674, 22.7617],
  [91.3601, 22.7434],
  [91.2908, 22.7384],
  [91.3328, 22.7202],
  [91.3174, 22.6972],
  [91.2448, 22.6984],
  [91.2434, 22.7202],
  [91.1819, 22.7265],
  [91.1992, 22.6988],
  [91.169, 22.6849],
  [91.2534, 22.6607],
  [91.2645, 22.6408],
  [91.2416, 22.5754],
  [91.2027, 22.5432],
  [91.1273, 22.527],
  [91.0892, 22.5444],
  [91.0183, 22.6831],
  [91.0038, 22.6787],
  [91.0379, 22.5886],
  [90.9851, 22.5744],
  [90.9417, 22.5859],
  [90.8765, 22.6828],
  [90.8359, 22.6849],
  [90.7949, 22.7414],
  [90.7175, 22.9406],
  [90.6294, 23.0644],
  [90.6673, 23.2676],
  [90.6509, 23.3002],
  [90.6611, 23.3223],
  [90.6284, 23.3215],
  [90.6113, 23.3425],
  [90.6003, 23.4499],
  [90.6333, 23.4868],
  [90.715, 23.4984],
  [90.7158, 23.5351],
  [90.6782, 23.5551],
  [90.7022, 23.5284],
  [90.6953, 23.5079],
  [90.6668, 23.5182],
  [90.6078, 23.4911],
  [90.5921, 23.5625],
  [90.6333, 23.5966],
  [90.612, 23.6061],
  [90.5588, 23.5904],
  [90.5472, 23.5638],
  [90.4763, 23.583],
  [90.4984, 23.5573],
  [90.5768, 23.5395],
  [90.5789, 23.4499],
  [90.5519, 23.385],
  [90.2494, 23.4554],
  [90.3885, 23.3724],
  [90.4285, 23.3261],
  [90.5691, 23.295],
  [90.6108, 23.2308],
  [90.6145, 23.1955],
  [90.5935, 23.2199],
  [90.5991, 23.1175],
  [90.572, 23.0817],
  [90.5537, 23.0786],
  [90.5588, 23.1107],
  [90.5228, 23.0801],
  [90.4536, 23.0723],
  [90.4489, 23.0557],
  [90.5104, 22.9182],
  [90.54, 22.8966],
  [90.4763, 22.8841],
  [90.436, 22.8006],
  [90.408, 22.7947],
  [90.4199, 22.7683],
  [90.3726, 22.7614],
  [90.4352, 22.7333],
  [90.4617, 22.7488],
  [90.4929, 22.7099],
  [90.5127, 22.6115],
  [90.4592, 22.5793],
  [90.4421, 22.4936],
  [90.4725, 22.5756],
  [90.5241, 22.5831],
  [90.5597, 22.5623],
  [90.6066, 22.4658],
  [90.5862, 22.4732],
  [90.6184, 22.3767],
  [90.6135, 22.3149],
  [90.558, 22.2034],
  [90.5011, 22.1767],
  [90.45, 22.0808],
  [90.4085, 22.0859],
  [90.3875, 22.1026],
  [90.4216, 22.1989],
  [90.4109, 22.2842],
  [90.3943, 22.2882],
  [90.3909, 22.2088],
  [90.4129, 22.178],
  [90.3322, 22.0381],
  [90.2727, 21.8574],
  [90.1848, 21.801],
  [90.0929, 21.8303],
  [90.1262, 21.932],
  [90.0669, 21.8534],
  [90.0244, 21.8625],
  [90.0339, 21.9435],
  [90.0858, 22.0002],
  [90.1056, 22.0543],
  [90.1641, 22.1132],
  [90.2162, 22.1232],
  [90.2391, 22.1723],
  [90.2361, 22.1989],
  [90.2058, 22.133],
  [90.1483, 22.1108],
  [90.0381, 21.9866],
  [89.9963, 22.0002],
  [90.0354, 22.0598],
  [90.0427, 22.1313],
  [90.0784, 22.164],
  [90.0526, 22.1555],
  [89.9828, 22.0446],
  [89.9828, 21.9866],
  [89.9678, 21.9746],
  [89.9344, 21.9977],
  [89.8768, 22.2747],
  [90.0101, 22.4902],
  [89.9204, 22.3918],
  [89.8729, 22.4186],
  [89.8804, 22.3422],
  [89.8387, 22.2678],
  [89.8713, 22.0919],
  [89.8462, 22.0706],
  [89.7972, 22.076],
  [89.8479, 22.0339],
  [89.8462, 21.9723],
  [89.8804, 21.8842],
  [89.7864, 21.8289],
  [89.7659, 21.8467],
  [89.7092, 21.8301],
  [89.6613, 21.8494],
  [89.647, 21.9251],
  [89.619, 21.8699],
  [89.7007, 21.7849],
  [89.6682, 21.7509],
  [89.5955, 21.76],
  [89.606, 21.7191],
  [89.5869, 21.7008],
  [89.5371, 21.7538],
  [89.5274, 21.8128],
  [89.5645, 21.8842],
  [89.5713, 22.0312],
  [89.606, 22.1132],
  [89.5672, 22.2028],
  [89.606, 22.2569],
  [89.6265, 22.336],
  [89.5492, 22.2053],
  [89.5794, 22.1132],
  [89.5514, 21.994],
  [89.5329, 21.9847],
  [89.5371, 22.089],
  [89.492, 22.1884],
  [89.493, 22.2735],
  [89.4758, 22.2951],
  [89.4758, 22.1026],
  [89.4942, 22.1369],
  [89.5183, 22.081],
  [89.4904, 22.024],
  [89.5218, 21.9623],
  [89.5125, 21.9091],
  [89.4646, 21.8592],
  [89.4658, 21.7813],
  [89.4064, 21.7174],
  [89.3696, 21.7433],
  [89.3659, 21.8289],
  [89.387, 21.9012],
  [89.3522, 21.9723],
  [89.356, 21.8925],
  [89.3013, 21.8094],
  [89.2839, 21.8221],
  [89.2947, 21.7578],
  [89.2737, 21.6988],
  [89.2975, 21.6781],
  [89.2487, 21.6459],
  [89.2105, 21.6541],
  [89.2207, 21.7094],
  [89.1877, 21.8115],
  [89.1546, 21.7869],
  [89.1047, 21.8159],
  [89.0778, 21.9143],
  [89.0915, 22.0002],
  [89.0574, 22.0651],
  [89.0762, 22.175],
  [89.0091, 22.2856],
  [89.0235, 22.2948],
  [89.0015, 22.3337],
  [88.9895, 22.3967],
  [89.0025, 22.428],
  [88.971, 22.5279],
  [88.928, 22.5481],
  [88.9564, 22.585],
  [88.9081, 22.7408],
  [88.9369, 22.7823],
  [88.9445, 22.848],
  [88.8935, 22.8796],
  [88.8388, 22.9758],
  [88.8513, 23.0751],
  [88.96, 23.2027],
  [88.8227, 23.238],
  [88.7757, 23.2216],
  [88.6966, 23.2524],
  [88.6859, 23.2933],
  [88.7702, 23.4888],
  [88.7568, 23.4988],
  [88.7194, 23.4681],
  [88.6092, 23.5888],
  [88.5714, 23.5923],
  [88.5664, 23.6287],
  [88.5416, 23.6388],
  [88.5776, 23.8552],
  [88.6531, 23.8698],
  [88.7142, 23.9252],
  [88.7016, 23.9953],
  [88.725, 24.0384],
  [88.6741, 24.0826],
  [88.6781, 24.1543],
  [88.7464, 24.2141],
  [88.7375, 24.2871],
  [88.6601, 24.3383],
  [88.6345, 24.2941],
  [88.4754, 24.3155],
  [88.4013, 24.3694],
  [88.1099, 24.501],
  [88.086, 24.5367],
  [88.0818, 24.5999],
  [88.0218, 24.6456],
  [88.0532, 24.755],
  [88.077, 24.7612],
  [88.141, 24.8446],
  [88.1149, 24.9164],
  [88.2024, 24.9453],
  [88.2428, 24.8806],
  [88.3228, 24.8747],
  [88.4258, 25.0511],
  [88.4413, 25.1897],
  [88.5594, 25.1708],
  [88.6343, 25.1923],
  [88.7842, 25.1609],
  [88.8171, 25.1968],
  [88.9242, 25.1679],
  [88.9096, 25.1892],
  [88.9279, 25.1927],
  [88.9291, 25.2294],
  [88.9849, 25.285],
  [88.9753, 25.3026],
  [88.9029, 25.3034],
  [88.842, 25.3328],
  [88.7996, 25.3956],
  [88.8149, 25.4586],
  [88.7723, 25.502],
  [88.742, 25.5128],
  [88.7426, 25.4952],
  [88.6663, 25.4629],
  [88.5303, 25.5002],
  [88.4238, 25.5922],
  [88.4194, 25.6538],
  [88.2599, 25.789],
  [88.2275, 25.8043],
  [88.1277, 25.7749],
  [88.0876, 25.831],
  [88.0744, 25.9081],
  [88.151, 26.0313],
  [88.1399, 26.0853],
  [88.1638, 26.1405],
  [88.2189, 26.1802],
  [88.3263, 26.2159],
  [88.3366, 26.2818],
  [88.4317, 26.3622],
  [88.4976, 26.3527],
  [88.4611, 26.4539],
  [88.3536, 26.4846],
  [88.3356, 26.4482],
  [88.3151, 26.4651],
  [88.3853, 26.6235],
  [88.411, 26.5451],
  [88.6563, 26.4151],
  [88.6522, 26.3915],
  [88.7122, 26.3283],
  [88.6458, 26.276],
  [88.6634, 26.2644],
  [88.7479, 26.2925],
  [88.784, 26.2799],
  [88.8248, 26.2266],
  [88.8758, 26.2774],
  [88.9753, 26.2243],
  [89.0195, 26.2345],
  [89.0397, 26.2472],
  [89.0044, 26.2757],
  [89.008, 26.2972],
  [88.9817, 26.2918],
  [88.966, 26.3285],
  [88.9124, 26.3489],
  [88.8912, 26.3858],
  [88.941, 26.4394],
  [89.0437, 26.3811],
  [89.0552, 26.347],
  [89.1029, 26.3273],
  [89.0948, 26.3095],
  [89.0689, 26.3143],
  [89.0694, 26.2874],
  [89.1192, 26.1466],
  [89.2126, 26.099],
  [89.2376, 26.0581],
  [89.3353, 25.9986],
  [89.3914, 26.0155],
  [89.4113, 25.9932],
  [89.4856, 25.9987],
  [89.5417, 25.9597],
  [89.5592, 26.0222],
  [89.6114, 26.0722],
  [89.567, 26.0871],
  [89.6042, 26.1207],
  [89.5628, 26.1426],
  [89.5868, 26.1692],
  [89.6144, 26.1642],
  [89.6133, 26.2194],
  [89.653, 26.2227],
  [89.6558, 26.1613],
  [89.7185, 26.1455],
  [89.7558, 26.0324],
  [89.7894, 25.988],
  [89.8141, 25.9961],
  [89.8287, 25.9794],
  [89.792, 25.9491],
  [89.8344, 25.9318],
  [89.7831, 25.8144],
  [89.8344, 25.6348],
  [89.795, 25.3742],
  [89.819, 25.285],
  [89.9081, 25.2969],
  [90.1301, 25.2116],
  [90.3646, 25.15],
  [90.6234, 25.1714],
  [90.7077, 25.153],
  [90.754, 25.1676],
  [90.822, 25.1423],
  [91.2357, 25.2019],
  [91.4319, 25.138],
  [91.5661, 25.16],
  [91.5962, 25.1289],
  [91.6387, 25.124],
  [91.7301, 25.1675],
  [92.034, 25.1817],
  [92.1727, 25.132],
  [92.2337, 25.085],
  [92.3035, 25.0743],
  [92.4838, 24.9282],
  [92.4945, 24.8949],
  [92.4777, 24.8639],
  [92.3807, 24.8367],
  [92.2904, 24.8913],
  [92.223, 24.8914],
  [92.2338, 24.7775],
  [92.1636, 24.5592],
  [92.1109, 24.5143],
  [92.1076, 24.406],
  [92.0627, 24.371],
  [91.95, 24.3754],
  [91.9722, 24.3161],
  [91.8998, 24.338],
  [91.9059, 24.2606],
  [91.8771, 24.158],
  [91.8442, 24.1553],
  [91.8138, 24.1831],
  [91.807, 24.2211],
  [91.7329, 24.2343],
  [91.7326, 24.1643],
  [91.7037, 24.1429],
  [91.6662, 24.1559],
  [91.6291, 24.2153],
  [91.606, 24.1189],
  [91.5818, 24.0961],
  [91.363, 24.0998],
  [91.3391, 23.9952],
  [91.2741, 23.9882],
  [91.2163, 23.9084],
  [91.2282, 23.845],
  [91.2052, 23.7716],
  [91.1323, 23.7206],
  [91.1752, 23.6703],
  [91.1397, 23.6536],
  [91.1408, 23.6121],
  [91.1951, 23.4952],
  [91.2273, 23.4688],
  [91.2581, 23.3736],
  [91.2873, 23.3556],
  [91.2658, 23.3294],
  [91.3135, 23.101],
  [91.3709, 23.0628],
  [91.3557, 23.2241],
  [91.3721, 23.2619],
  [91.3943, 23.2627],
  [91.4636, 23.1841],
  [91.5236, 22.9952],
  [91.5768, 22.9688],
  [91.5861, 22.9445],
  [91.6167, 22.9781],
  [91.707, 22.9954],
  [91.7956, 23.0895],
  [91.7437, 23.2723],
  [91.8177, 23.4009],
  [91.9051, 23.4466],
  [91.9408, 23.4952],
  [91.9227, 23.723],
  [92.019, 23.6401],
  [92.1084, 23.7182],
  [92.1508, 23.7317],
  [92.1943, 23.6481],
  [92.2221, 23.7078],
  [92.2593, 23.7065],
  [92.2528, 23.6093],
  [92.3015, 23.4318],
  [92.3486, 23.3541],
  [92.3575, 23.2789],
  [92.3276, 23.1714],
  [92.3599, 22.9266],
  [92.4263, 22.8711],
  [92.442, 22.7629],
  [92.4959, 22.6956],
  [92.5008, 22.5605],
  [92.577, 22.1687],
  [92.5756, 22.1433],
  [92.5373, 22.1286],
  [92.5759, 21.9776],
];

/**
 * The country’s extent, as the outline above gives it — derived from the same
 * cut, as PAKISTAN_BOUNDS is, so every point in the ring is inside it. A home
 * view and a box to reject a transposed coordinate against; not a boundary.
 */
export const BANGLADESH_BOUNDS: GeoExtent = {
  minLat: 20.7387,
  maxLat: 26.6235,
  minLng: 88.0218,
  maxLng: 92.6392,
};
